import React, { useEffect, useMemo, useRef, useState } from "react";
import { CountUp } from "countup.js";

function toFiniteNumber(value) {
  const numberValue = typeof value === "number" ? value : Number(value);
  return Number.isFinite(numberValue) ? numberValue : null;
}

function usePrefersReducedMotion() {
  const [reducedMotion, setReducedMotion] = useState(() =>
    typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
      : false
  );

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const handleChange = (event) => setReducedMotion(event.matches);

    if (mediaQuery.addEventListener) mediaQuery.addEventListener("change", handleChange);
    else mediaQuery.addListener(handleChange);

    return () => {
      if (mediaQuery.removeEventListener) mediaQuery.removeEventListener("change", handleChange);
      else mediaQuery.removeListener(handleChange);
    };
  }, []);

  return reducedMotion;
}

export default function NumberFlowValue({
  value,
  locales = "pt-BR",
  format,
  prefix,
  suffix,
  fallback = "—",
  className = "",
  duration = 1.35,
  ...props
}) {
  const elementRef = useRef(null);
  const previousValueRef = useRef(0);
  const hasAnimatedRef = useRef(false);
  const reducedMotion = usePrefersReducedMotion();
  const numericValue = toFiniteNumber(value);
  const formatKey = JSON.stringify(format || {});

  const numberPresentation = useMemo(() => {
    try {
      const formatter = new Intl.NumberFormat(locales, JSON.parse(formatKey));
      const decimalPlaces = formatter.resolvedOptions().maximumFractionDigits;
      return {
        decimalPlaces,
        formatValue: (number) => `${prefix ?? ""}${formatter.format(number)}${suffix ?? ""}`,
      };
    } catch {
      return {
        decimalPlaces: 0,
        formatValue: (number) => `${prefix ?? ""}${number}${suffix ?? ""}`,
      };
    }
  }, [formatKey, locales, prefix, suffix]);

  const fallbackLabel = numericValue == null ? fallback : numberPresentation.formatValue(numericValue);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return undefined;

    if (numericValue == null || reducedMotion) {
      element.textContent = fallbackLabel;
      if (numericValue != null) {
        previousValueRef.current = numericValue;
        hasAnimatedRef.current = true;
      }
      return undefined;
    }

    const countUp = new CountUp(element, numericValue, {
      startVal: hasAnimatedRef.current ? previousValueRef.current : 0,
      duration,
      decimalPlaces: numberPresentation.decimalPlaces,
      formattingFn: numberPresentation.formatValue,
      useEasing: true,
      useGrouping: false,
    });

    previousValueRef.current = numericValue;
    hasAnimatedRef.current = true;

    if (countUp.error) {
      element.textContent = fallbackLabel;
      return () => countUp.onDestroy();
    }

    countUp.start();
    return () => countUp.onDestroy();
  }, [duration, fallbackLabel, numberPresentation, numericValue, reducedMotion]);

  return (
    <span ref={elementRef} className={className} aria-label={fallbackLabel} {...props}>
      {fallbackLabel}
    </span>
  );
}
